// src/pages/IrsPaymentOptions.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { useWizardFormId, withForm, formBelongsToCompany } from '../utils/wizardForm';

function IrsPaymentOptions() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js)
  const formId = useWizardFormId();

  // company data
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // payment option (теперь доступны все три)
  const [selectedOption, setSelectedOption] = useState('EFW');
  // true когда весь налог $0 (все машины category W) — платить IRS нечего
  const [zeroTax, setZeroTax] = useState(false);

  // EFW fields
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccount, setConfirmAccount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [accountType, setAccountType] = useState('Checking');

  const ROUTING_RE = /^(?:0[1-9]|1[0-2]|2[1-9]|3[0-2])[0-9]{7}$/;

  // validation
  const [errors, setErrors] = useState({
    routingNumber: '',
    accountNumber: '',
    confirmAccount: '',
    phoneNumber: '',
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const token = localStorage.getItem('token');

        // company
        const compRes = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!compRes.ok) throw new Error('Error loading company data');
        setCompany(await compRes.json());

        // form
        if (!formId) return;

        const formRes = await fetch(`/api/form2290/${formId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!formRes.ok) throw new Error('Error loading form data');
        const formData = await formRes.json();
        if (!formBelongsToCompany(formData, id)) {
          navigate(`/company/${id}`, { replace: true });
          return;
        }

        // Налог $0 (все машины tax-suspended, category W) — оплата IRS не нужна
        const details = formData.vehicles_detail || [];
        const totalTax = details.reduce(
          (sum, v) => sum + (parseFloat(v.taxable_weight) || 0),
          0
        );
        setZeroTax(details.length > 0 && totalTax === 0);

        // Инициализация выбранного метода оплаты
        if (formData.irs_payment_option) {
          setSelectedOption(formData.irs_payment_option);
        }

        // Если EFW — подставить сохранённые реквизиты
        if (formData.irs_payment_option === 'EFW' && formData.payment_info) {
          try {
            const info = JSON.parse(formData.payment_info);
            setRoutingNumber(info.routing_number || '');
            setAccountNumber(info.account_number || '');
            setConfirmAccount(info.account_number || '');
            setPhoneNumber(info.phone_number || '');
            setAccountType(info.account_type || 'Checking');
          } catch (e) {
            console.warn('Invalid payment_info JSON', e);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, formId, navigate]);

  const validateEfw = () => {
    const errs = { routingNumber: '', accountNumber: '', confirmAccount: '', phoneNumber: '' };

    if (!ROUTING_RE.test(routingNumber)) {
      errs.routingNumber = 'Routing number must be 9 digits and start with 01–12 or 21–32';
    }
    if (!/^\d{4,17}$/.test(accountNumber)) {
      errs.accountNumber = 'Account number must be 4–17 digits';
    }
    if (confirmAccount !== accountNumber) {
      errs.confirmAccount = 'Account numbers do not match';
    }
    if (!/^\d{10}$/.test(phoneNumber)) {
      errs.phoneNumber = 'Phone number must be exactly 10 digits';
    }

    setErrors(errs);
    return Object.values(errs).every(msg => msg === '');
  };

  const isEfwValidLive =
    ROUTING_RE.test(routingNumber) &&
    /^\d{4,17}$/.test(accountNumber) &&
    confirmAccount === accountNumber &&
    /^\d{10}$/.test(phoneNumber);

  const handleContinueFiling = async () => {
    setSubmitAttempted(true);

    // Для EFW — валидируем поля (кроме zero-tax: платить нечего)
    if (!zeroTax && selectedOption === 'EFW' && !validateEfw()) return;

    try {
      const token = localStorage.getItem('token');
      if (!formId) throw new Error('Form ID not found');

      const body = zeroTax
        ? { irs_payment_option: null, payment_info: null, step: 3 }
        : {
            irs_payment_option: selectedOption,
            step: 3,
          };

      if (!zeroTax && selectedOption === 'EFW') {
        body.payment_info = JSON.stringify({
          routing_number: routingNumber,
          account_number: accountNumber,
          phone_number: phoneNumber,
          account_type: accountType,
        });
      } else {
        // ВАЖНО: очищаем старые банковские данные на бэкенде
        body.payment_info = null;
      }

      const res = await fetch(`/api/form2290/${formId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Error updating payment option');

      navigate(withForm(`/company/${id}/file-form/form-summary`, formId));
    } catch (e) {
      console.error(e);
    }
  };

  // При смене метода — сброс подсветки ошибок EFW (но не трогаем введённые значения)
  const handleSelectOption = (opt) => {
    setSelectedOption(opt);
    setSubmitAttempted(false);
    setErrors({ routingNumber: '', accountNumber: '', confirmAccount: '', phoneNumber: '' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 3;

  const isRoutingValid = ROUTING_RE.test(routingNumber);
  const isAccountValid = /^\d{4,17}$/.test(accountNumber);
  const isConfirmValid = confirmAccount === accountNumber;
  const isPhoneValid = /^\d{10}$/.test(phoneNumber);

  const routingInlineError =
    submitAttempted
      ? errors.routingNumber
      : (routingNumber && !isRoutingValid
        ? 'Invalid routing number (must be 9 digits starting with 01–12 or 21–32)'
        : '');

  const accountInlineError =
    submitAttempted
      ? errors.accountNumber
      : (accountNumber && !isAccountValid
        ? 'Invalid account number (4–17 digits)'
        : '');

  const confirmInlineError =
    submitAttempted
      ? errors.confirmAccount
      : (confirmAccount && !isConfirmValid
        ? 'Account numbers do not match'
        : '');

  const phoneInlineError =
    submitAttempted
      ? errors.phoneNumber
      : (phoneNumber && !isPhoneValid
        ? 'Invalid phone number (10 digits)'
        : '');

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderLogged />

      <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* form */}
      <main className="flex-1 bg-[#E4F2FF] py-6 px-4">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-[#0F2C5E]">
              IRS Payment Options
            </h1>
          </div>

          <div className="p-6 space-y-6">
            {zeroTax && (
              <div className="p-4 bg-green-50 border border-green-300 rounded text-sm">
                <p className="font-semibold text-green-800 mb-1">
                  No tax due — no IRS payment needed.
                </p>
                <p className="text-gray-700">
                  All vehicles on this return are tax-suspended (category W), so
                  the balance due to the IRS is $0. Click Continue Filing to
                  proceed.
                </p>
              </div>
            )}

            {!zeroTax && (
            <>
            <p className="text-gray-700">
              Choose how you want to pay the IRS.
            </p>

            {/* EFW (enabled) */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                value="EFW"
                checked={selectedOption === 'EFW'}
                onChange={() => handleSelectOption('EFW')}
                className="mt-1 h-5 w-5 text-[#0F2C5E]"
              />
              <span className="text-gray-800">
                <strong>Electronic Funds Withdrawal (EFW)</strong>
                <br />
                Authorize the IRS to debit from your bank account on submission.
              </span>
            </label>

            {/* EFTPS (enabled, без форм) */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                value="EFTPS"
                checked={selectedOption === 'EFTPS'}
                onChange={() => handleSelectOption('EFTPS')}
                className="mt-1 h-5 w-5 text-[#0F2C5E] flex-shrink-0"
              />
              <span className="text-gray-800 leading-relaxed">
                <strong>EFTPS — Electronic Federal Tax Payment System</strong>
                <br />
                You can pay the 2290 tax due electronically to the IRS by visiting{" "}
                <a
                  href="https://www.eftps.gov"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  eftps.gov
                </a>
                . It will take a few minutes if you already have an account with EFTPS. If you don’t have one, it may take 7–10 business days to set up your account. Payments made through EFTPS should be scheduled between 4–5 business days so that the payment can be received on time.
              </span>
            </label>

            {/* Credit Card / Debit Card (enabled, без форм) */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                value="CCDC"
                checked={selectedOption === 'CCDC'}
                onChange={() => handleSelectOption('CCDC')}
                className="mt-1 h-5 w-5 text-[#0F2C5E] flex-shrink-0"
              />
              <span className="text-gray-800 leading-relaxed">
                <strong>Credit Card / Debit Card</strong>
                <br />
                Please follow the below given instructions to pay via Credit or Debit Card to the IRS.
                <ol className="list-decimal list-inside mt-1 space-y-1">
                  <li>
                    You can pay the 2290 tax due electronically to the IRS by using a credit or debit card.
                  </li>
                  <li>
                    Go to{" "}
                    <a
                      href="https://www.irs.gov/paybycard"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline"
                    >
                      IRS.gov/PayByCard
                    </a>
                    . A convenience fee is charged by these service providers.
                  </li>
                  <li>
                    If you make your payment using a credit or debit card, don’t include the payment voucher and
                    make sure to check the "Credit or Debit Card" box on line 6 of Form 2290.
                  </li>
                </ol>
              </span>
            </label>
            {/* EFW fields — показываем только при выбранном EFW */}
            {selectedOption === 'EFW' && (
              <div className="mt-4 p-4 border border-gray-200 bg-white space-y-4">
                <div>
                  <label className="block text-[#0F2C5E] font-medium">Routing Number</label>
                  <input
                    type="text"
                    maxLength={9}
                    value={routingNumber}
                    onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                    aria-invalid={!isRoutingValid}
                    className={`mt-1 block w-full p-2 border ${
                      isRoutingValid ? 'border-gray-300' : 'border-red-500'
                    } focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E]`}
                    placeholder="123456789"
                  />
                  {routingInlineError && (
                    <p className="mt-1 text-sm text-red-600">{routingInlineError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#0F2C5E] font-medium">Account Number</label>
                  <input
                    type="text"
                    maxLength={17}
                    value={accountNumber}
                    onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                    className={`mt-1 block w-full p-2 border ${
                      /^\d{4,17}$/.test(accountNumber) ? 'border-gray-300' : 'border-red-500'
                    } focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E]`}
                    placeholder="12345678901234567"
                  />
                  {accountInlineError && (
                    <p className="mt-1 text-sm text-red-600">{accountInlineError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#0F2C5E] font-medium">Confirm Account Number</label>
                  <input
                    type="text"
                    maxLength={17}
                    value={confirmAccount}
                    onChange={e => setConfirmAccount(e.target.value.replace(/\D/g, ''))}
                    aria-invalid={!isConfirmValid}
                    className={`mt-1 block w-full p-2 border ${
                      isConfirmValid ? 'border-gray-300' : 'border-red-500'
                    } focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E]`}
                    placeholder="Re-enter account"
                  />
                  {confirmInlineError && (
                    <p className="mt-1 text-sm text-red-600">{confirmInlineError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#0F2C5E] font-medium">Phone Number</label>
                  <input
                    type="text"
                    maxLength={15}
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    aria-invalid={!isPhoneValid}
                    className={`mt-1 block w-full p-2 border ${
                      isPhoneValid ? 'border-gray-300' : 'border-red-500'
                    } focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E]`}
                    placeholder="(555) 555-5555"
                  />
                  {phoneInlineError && (
                    <p className="mt-1 text-sm text-red-600">{phoneInlineError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[#0F2C5E] font-medium">Account Type</label>
                  <select
                    value={accountType}
                    onChange={e => setAccountType(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E]"
                  >
                    <option value="Checking">Checking</option>
                    <option value="Savings">Savings</option>
                  </select>
                </div>
              </div>
            )}
            </>
            )}

            {/* navigation buttons */}
            <div className="flex flex-col sm:flex-row justify-between mt-8 gap-4">
              <button
                onClick={() => navigate(withForm(`/company/${id}/file-form/vehicles-tax`, formId))}
                className="w-full sm:w-auto bg-gray-300 text-gray-700 px-4 py-2 hover:bg-gray-400 transition"
              >
                Back
              </button>
              <button
                onClick={handleContinueFiling}
                disabled={!zeroTax && selectedOption === 'EFW' && !isEfwValidLive}
                className="w-full sm:w-auto bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition disabled:opacity-50"
              >
                Continue Filing
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default IrsPaymentOptions;
